import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { billingAPI, Invoice } from '../../services/api';

export const InvoiceHistory: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const response = await billingAPI.getInvoices(20);
      setInvoices(response.data.invoices);
    } catch (err) {
      setError(t('billing.errors.loadInvoices'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'paid':
        return isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-green-100 text-green-800';
      case 'open':
        return isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-yellow-100 text-yellow-800';
      case 'void':
        return isDark ? 'bg-slate-600/50 text-slate-300' : 'bg-gray-100 text-gray-800';
      case 'uncollectible':
        return isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-800';
      default:
        return isDark ? 'bg-slate-600/50 text-slate-300' : 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`rounded-lg p-4 animate-pulse ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
            <div className={`h-5 rounded w-1/4 mb-2 ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}></div>
            <div className={`h-4 rounded w-1/3 ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`px-4 py-3 rounded-xl m-4 ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className={`text-center py-8 m-4 rounded-xl ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('billing.noInvoices')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className={`min-w-full divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
        <thead className={isDark ? 'bg-slate-800/50' : 'bg-gray-50'}>
          <tr>
            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('billing.invoice')}
            </th>
            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('billing.date')}
            </th>
            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('billing.amount')}
            </th>
            <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('billing.status')}
            </th>
            <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('billing.actions')}
            </th>
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
          {invoices.map((invoice) => (
            <tr key={invoice.id} className={`transition-colors ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {invoice.invoice_number || `#${invoice.id}`}
                </span>
              </td>
              <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {formatDate(invoice.created_at)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {invoice.formatted_total}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                    invoice.status
                  )}`}
                >
                  {t(`billing.invoiceStatus.${invoice.status}`)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                {invoice.hosted_invoice_url && (
                  <a
                    href={invoice.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-500 hover:text-cyan-400 transition-colors"
                  >
                    {t('billing.view')}
                  </a>
                )}
                {invoice.invoice_pdf_url && (
                  <a
                    href={invoice.invoice_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-500 hover:text-cyan-400 transition-colors"
                  >
                    {t('billing.download')}
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InvoiceHistory;
